// frontend/src/components/VerifyEmail.js
import React, { useState, useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import axios from "axios";
import "./VerifyEmail.css";

/**
 * VerifyEmail component for handling email verification
 * @returns {JSX.Element} - Rendered component
 */
const VerifyEmail = () => {
  const [status, setStatus] = useState("verifying");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [verificationContext, setVerificationContext] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { pendingGiftCode } = useAuth();

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        // Get token from URL query parameters
        const params = new URLSearchParams(location.search);
        const token = params.get("token");

        if (!token) {
          setStatus("error");
          setMessage("Verification token is missing.");
          return;
        }

        // Call API to verify email
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/auth/verify-email`,
          { params: { token } }
        );

        setStatus("success");
        setMessage(response.data.message);
        setVerificationContext({
          corporateProgramStatus: response.data.corporateProgramStatus || null,
          introOffersEligible: response.data.introOffersEligible !== false,
        });
      } catch (error) {
        setStatus("error");
        setMessage(
          error.response?.data?.message || "Email verification failed."
        );
      }
    };

    verifyEmail();
  }, [location.search]);

  const handleResendVerification = async (e) => {
    e.preventDefault();

    if (!email) {
      setMessage("Please enter your email address.");
      return;
    }

    try {
      setStatus("sending");

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/resend-verification`,
        { email }
      );

      setStatus("sent");
      setMessage(response.data.message);
    } catch (error) {
      setStatus("error");
      setMessage(
        error.response?.data?.message || "Failed to resend verification email."
      );
    }
  };

  const corporateProgramStatus =
    verificationContext?.corporateProgramStatus || null;
  const isCorporateEnrolled = Boolean(corporateProgramStatus?.enrolled);

  return (
    <div className="verify-email-page">
      <div className="verify-email-container">
        <div className="logo-container">
          <img
            src="/images/dairls-logo-app.svg"
            alt="Diviti Adora Studio Logo"
            className="studio-logo"
          />
        </div>

        <h1>Email Verification</h1>
        <div className="verification-card">
          {status === "verifying" && (
            <div className="verifying">
              <div className="spinner"></div>
              <h2>Verifying Your Email</h2>
              <p>Please wait while we verify your email address...</p>
            </div>
          )}

          {status === "success" && (
            <div className="success">
              <div className="success-icon">✓</div>
              <h2>Email Verified!</h2>
              <p className="success-message">{message}</p>

              <div className="setup-checklist">
                <h3>Before You Book Online</h3>
                <ul className="setup-checklist-list">
                  <li>Add your contact number to your profile.</li>
                  <li>Add a saved payment method to your account.</li>
                  <li>Book sessions at least 30 minutes in advance.</li>
                  <li>
                    Most members can book up to 10 days ahead. Priority members
                    can book up to 14 days ahead.
                  </li>
                </ul>
                <p className="setup-checklist-note">
                  Your phone number is required for online booking, and a saved
                  payment method helps us process paid bookings and add-ons
                  smoothly.
                </p>
              </div>

              {isCorporateEnrolled ? (
                <div className="complimentary-sessions">
                  <h3>Your Corporate Program Is Active</h3>
                  <p>
                    <strong>
                      {corporateProgramStatus.organizationCode} •{" "}
                      {corporateProgramStatus.programName}
                    </strong>
                  </p>
                  <p>
                    You are enrolled in your organization's corporate wellness
                    program and can now book eligible sessions through your
                    member dashboard.
                  </p>
                  <p>
                    <strong>Included sponsored sessions each month:</strong>{" "}
                    {
                      corporateProgramStatus.sponsoredSessionsPerEmployeePerMonth
                    }
                  </p>
                  <p>
                    <strong>Corporate rate after sponsored sessions are used:</strong>{" "}
                    ${Number(corporateProgramStatus.corporateRate || 0).toFixed(2)}
                  </p>
                  <p>
                    Customers enrolled in a corporate program are not eligible
                    for intro offers.
                  </p>
                  <p>
                    If your sponsored sessions are used up and your next booking
                    would fall to the corporate rate, make sure you have a saved
                    payment method on file before booking.
                  </p>
                </div>
              ) : (
                <div className="complimentary-sessions">
                  <h3>Your Complimentary Session</h3>
                  <p>
                    <strong>Congratulations!</strong> As a new member, you're
                    eligible for one complimentary Flexible Session you can use
                    for Infrared Sauna or Red Light Therapy.
                  </p>
                  <p>
                    <strong>What's next:</strong> You'll receive an email with
                    instructions on how to claim and book your complimentary
                    session. You can also visit our
                    <Link to="/drop-in-sessions">
                      {" "}
                      drop-in sessions page
                    </Link>{" "}
                    directly to claim your free session.
                  </p>
                  <p>
                    Before you book online, please make sure your profile
                    includes both a contact number and a saved payment method.
                  </p>
                </div>
              )}

              <p className="benefits-text">
                You now have access to all member features including:
              </p>
              <ul className="benefits-list">
                <li>Book sessions at Diviti Adora Studio</li>
                <li>Manage your membership</li>
                <li>Access exclusive member content</li>
                <li>Receive special offers and promotions</li>
              </ul>

              {pendingGiftCode ? (
                <>
                  <p className="gift-note">
                    <strong>You have a gift waiting for you!</strong> Please
                    sign in to redeem your gift.
                  </p>
                  <Link
                    to="/login"
                    className="login-button"
                    state={{ from: `/redeem/${pendingGiftCode}` }}
                  >
                    Sign In to Redeem Your Gift
                  </Link>
                </>
              ) : (
                <Link to="/login" className="login-button">
                  Sign In to Your Account
                </Link>
              )}
            </div>
          )}

          {status === "error" && (
            <div className="error">
              <div className="error-icon">!</div>
              <h2>Verification Failed</h2>
              <p className="error-message">{message}</p>

              <div className="resend-section">
                <h3>Resend Verification Email</h3>
                <p>
                  If your verification link has expired or is invalid, you can
                  request a new one:
                </p>
                <form onSubmit={handleResendVerification}>
                  <input
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <button type="submit">Resend Verification Email</button>
                </form>
              </div>

              <div className="links">
                <Link to="/login">Back to Login</Link>
                <span className="separator">|</span>
                <Link to="/">Return to Home</Link>
              </div>
            </div>
          )}

          {status === "sending" && (
            <div className="sending">
              <div className="spinner"></div>
              <h2>Sending Verification Email</h2>
              <p>Please wait while we send a new verification email...</p>
            </div>
          )}

          {status === "sent" && (
            <div className="success">
              <div className="success-icon">✓</div>
              <h2>Verification Email Sent</h2>
              <p className="success-message">{message}</p>
              <p className="next-steps">
                Please check your email inbox and click on the verification link
                to complete the process. If you don't see the email, please
                check your spam or junk folder.
              </p>
              <div className="links">
                <Link to="/login">Back to Login</Link>
                <span className="separator">|</span>
                <Link to="/">Return to Home</Link>
              </div>
            </div>
          )}
        </div>

        <div className="contact-support">
          <p>
            Having trouble? <Link to="/contact">Contact our support team</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
